import { get } from 'lodash';
import dayjs from 'dayjs';

const NO_ANSWER_LABEL = '-';

class TableSorter {
  constructor(
    studentItems,
    resultsByUser,
    studentSessionsByUserId,
    responsesByUser,
    scoringRubricScoresByUser,
    itemResultsByUser,
    rubricResultByUser,
    listOfTEIs,
    isPreApSubject,
    studentScoringResultByUser,
    teacherScoringResultByUser,
    mcqScoresByUsers,
    teacherFRQScoresByUsers,
    studentFRQScoresByUsers,
    showScoringDropdown,
    selectedScoringDropdownItem,
    selectedView,
    userTimeSpents,
    userTimeSpentPerQuestion,
  ) {
    this.studentsItems = studentItems;
    this.userTimeSpents = userTimeSpents;
    this.userTimeSpentPerQuestion = userTimeSpentPerQuestion;
    this._unSortedStudent = studentItems.slice(0);
    this.resultsByUser = resultsByUser;
    this.studentSessionsByUserId = studentSessionsByUserId;
    this.responsesByUser = responsesByUser;
    this.scoringRubricScoresByUser = scoringRubricScoresByUser;
    this.itemResultsByUser = itemResultsByUser;
    this.rubricResultByUser = rubricResultByUser;
    this.listOfTEIs = listOfTEIs;
    this.isPreApSubject = isPreApSubject;
    this.studentScoringResultByUser = studentScoringResultByUser;
    this.teacherScoringResultByUser = teacherScoringResultByUser;
    this.mcqScoresByUsers = mcqScoresByUsers;
    this.teacherFRQScoresByUsers = teacherFRQScoresByUsers;
    this.studentFRQScoresByUsers = studentFRQScoresByUsers;
    this.showScoringDropdown = showScoringDropdown;
    this.selectedView = selectedView;
    this.selectedScoringDropdownItem = selectedScoringDropdownItem;
  }

  sortStudent(tableSort) {
    /**
     * tableSort format can be
     * responses_FRQ_VH962902_rubric_category_2_descending for FRQ
     *  or name_descending for normal Column
     */
    const [sortColumn, sortType, ...others] = tableSort.split('_');
    this.isAscending =
      sortType === 'ascending' || tableSort.includes('ascending');
    this.isUnsorted = sortType === 'unsorted' || tableSort.includes('unsorted');
    this.sortColumn = sortColumn;
    /**
     * FY-10855: There should be a secondary sort criterion if there is a tie on the first.
     * It should be name, ascending.
     */
    this.useTieSortOrder = false;

    if (this.isUnsorted) {
      return this._unSortedStudent;
    }
    let sortFn = () => {};
    if (sortColumn === 'name') {
      sortFn = this.sortByStudentName;
    }
    if (sortColumn === 'timeSpent') {
      sortFn = this.sortByTimeSpent();
    }
    if (sortColumn === 'submitted') {
      sortFn = this.sortBySubmitedDate;
    }
    if (sortColumn === 'pointEarned') {
      sortFn = this.sortByPointEarned;
    }
    if (sortColumn === 'responses') {
      // due to tableSort.split('_') sortType holds the questionType value eg MCQ/FRQ
      this.isUnsorted = others[others.length - 1] === 'unsorted';
      if (this.isUnsorted) {
        return this._unSortedStudent;
      }
      if (this.selectedView === 'time') {
        // sort by timeSpent per question
        const learnosityItemReference = others[0];
        sortFn = this.sortByTimeSpent(learnosityItemReference);
      } else {
        sortFn = this.sortByResponse(sortType, others);
      }
    }

    if (sortColumn === 'studentScores' || sortColumn === 'teacherScores') {
      sortFn = this.sortByStudentOrTeacherScores(sortColumn, null);
    }

    if (
      sortColumn === 'studentScoresWithLearnRef' ||
      sortColumn === 'teacherScoresWithLearnRef'
    ) {
      const [sortColumn, ...others] = tableSort.split('_');
      const learnosityItemReference = others
        .slice(0, others.length - 1)
        .join('_');
      const sortType = others[others.length - 1];
      this.isAscending = sortType === 'ascending';
      this.isUnsorted = sortType === 'unsorted';

      if (this.isUnsorted) {
        return this._unSortedStudent;
      }

      sortFn = this.sortByStudentOrTeacherScores(
        sortColumn,
        learnosityItemReference,
      );
    }

    return this.studentsItems.sort(sortFn.bind(this));
  }

  sortByStudentOrTeacherScores(sortBy, learnosityItemReference) {
    const getStudentsResult = (a, b) => {
      let studentAResult;
      let studentBResult;

      const isAnyMcqAttempted = (mcqData) => {
        const { allAttempts = [] } = mcqData;
        return allAttempts.some((attempt) => Boolean(attempt));
      };

      const addScores = (mcqData, frqData) => {
        if (!mcqData && !frqData) {
          return null;
        }

        const mcqDataScore = get(mcqData || {}, 'score', 0);
        const frqDataScore = get(frqData || {}, 'score', 0);

        return { score: mcqDataScore + frqDataScore };
      };

      const getTotalStudentResult = (studentFRQscores, studentMCQscores) => {
        if (!studentFRQscores) {
          return null;
        }

        if (studentMCQscores && isAnyMcqAttempted(studentMCQscores)) {
          return addScores(studentMCQscores, studentFRQscores);
        }

        return addScores(null, studentFRQscores);
      };

      if (sortBy === 'studentScoresWithLearnRef') {
        const resultsA =
          (this.studentScoringResultByUser[a.id] || {}).items || {};
        const resultsB =
          (this.studentScoringResultByUser[b.id] || {}).items || {};
        studentAResult = resultsA[learnosityItemReference];
        studentBResult = resultsB[learnosityItemReference];
      } else if (sortBy === 'teacherScoresWithLearnRef') {
        const resultsA =
          (this.teacherScoringResultByUser[a.id] || {}).items || {};
        const resultsB =
          (this.teacherScoringResultByUser[b.id] || {}).items || {};
        studentAResult = resultsA[learnosityItemReference];
        studentBResult = resultsB[learnosityItemReference];
      } else if (sortBy === 'studentScores') {
        const studentAMCQscores = this.mcqScoresByUsers[a.id];
        const studentAFRQscores = this.studentFRQScoresByUsers[a.id];
        studentAResult = getTotalStudentResult(
          studentAFRQscores,
          studentAMCQscores,
        );

        const studentBMCQscores = this.mcqScoresByUsers[b.id];
        const studentBFRQscores = this.studentFRQScoresByUsers[b.id];
        studentBResult = getTotalStudentResult(
          studentBFRQscores,
          studentBMCQscores,
        );
      } else if (sortBy === 'teacherScores') {
        const studentAMCQscores = this.mcqScoresByUsers[a.id];
        const studentAFRQscores = this.teacherFRQScoresByUsers[a.id];
        studentAResult = getTotalStudentResult(
          studentAFRQscores,
          studentAMCQscores,
        );

        const studentBMCQscores = this.mcqScoresByUsers[b.id];
        const studentBFRQscores = this.teacherFRQScoresByUsers[b.id];
        studentBResult = getTotalStudentResult(
          studentBFRQscores,
          studentBMCQscores,
        );
      }

      return [studentAResult, studentBResult];
    };

    const sortFn = (a, b) => {
      const [studentAResult, studentBResult] = getStudentsResult(a, b);
      let totalPointA = 0;
      let totalPointB = 0;
      this.useTieSortOrder = false;
      // for no result push to bottom
      const shouldStayBottom = TableSorter.putNullEmptyBelow(
        studentAResult,
        studentBResult,
      );
      if (shouldStayBottom) {
        return shouldStayBottom;
      }
      // when there is a No-Result tie
      if (shouldStayBottom === null) {
        this.useTieSortOrder = true;
        return this.sortByStudentName(a, b);
      }

      totalPointA = parseFloat(studentAResult.score);
      totalPointB = parseFloat(studentBResult.score);
      if (totalPointA === totalPointB) {
        // sort on student name if tie
        this.useTieSortOrder = true;
        return this.sortByStudentName(a, b);
      }
      if (this.isAscending) {
        return totalPointA < totalPointB ? -1 : 1;
      }
      return totalPointA < totalPointB ? 1 : -1;
    };

    return sortFn;
  }

  compare(a, b) {
    const shouldStayBottom = TableSorter.putNullEmptyBelow(a, b);
    if (shouldStayBottom) {
      return shouldStayBottom;
    }
    if (shouldStayBottom === null) {
      return 0;
    }
    const aUpper = a.toUpperCase();
    const bUpper = b.toUpperCase();

    if (aUpper === bUpper) {
      return 0;
    }

    if (this.useTieSortOrder) {
      return aUpper < bUpper ? -1 : 1;
    }
    // skipped questions or value with '-' should be placed before No-Result
    if (aUpper === NO_ANSWER_LABEL) {
      return 1;
    }
    if (bUpper === NO_ANSWER_LABEL) {
      return -1;
    }
    if (this.isAscending) {
      return aUpper < bUpper ? -1 : 1;
    }
    return aUpper < bUpper ? 1 : -1;
  }

  sortByTimeSpent = (learnosityItemReference) => {
    const compareFn = (a, b) => {
      let timeSpentA;
      let timeSpentB;

      if (learnosityItemReference) {
        timeSpentA =
          a.id in this.userTimeSpentPerQuestion
            ? this.userTimeSpentPerQuestion[a.id][learnosityItemReference]
            : null;
        timeSpentB =
          b.id in this.userTimeSpentPerQuestion
            ? this.userTimeSpentPerQuestion[b.id][learnosityItemReference]
            : null;
      } else {
        timeSpentA = this.userTimeSpents[a.id];
        timeSpentB = this.userTimeSpents[b.id];
      }
      if ((!timeSpentA && !timeSpentB) || timeSpentA === timeSpentB) {
        return 0;
      }
      if (!timeSpentA) {
        return 1;
      }
      if (!timeSpentB) {
        return -1;
      }
      let response = timeSpentA < timeSpentB ? -1 : 1;
      if (this.isAscending) {
        return response;
      }
      return response * -1;
    };
    return compareFn;
  };

  sortByStudentName(a, b) {
    if (a.lastName.toUpperCase() === b.lastName.toUpperCase()) {
      if (a.firstName.toUpperCase() === b.firstName.toUpperCase()) {
        return 0;
      }
      // sort by firstName if last name are same
      return this.compare(a.firstName, b.firstName);
    }
    return this.compare(a.lastName, b.lastName);
  }

  sortBySubmitedDate(a, b) {
    this.useTieSortOrder = false;
    let s1 = this.studentSessionsByUserId[a.id];
    let s2 = this.studentSessionsByUserId[b.id];

    if (!this.itemResultsByUser[a.id]) {
      s1 = null;
    }

    if (!this.itemResultsByUser[b.id]) {
      s2 = null;
    }
    // prevent null or empty values
    const shouldStayBottom = TableSorter.putNullEmptyBelow(s1, s2);
    if (shouldStayBottom) {
      return shouldStayBottom;
    }

    // when there is a No-Result tie
    if (shouldStayBottom === null) {
      this.useTieSortOrder = true;
      return this.sortByStudentName(a, b);
    }
    const date1 = dayjs(s1.submittedAt);
    const date2 = dayjs(s2.submittedAt);
    const diff = date1.diff(date2);
    if (diff === 0) {
      // sort on student name if tie
      this.useTieSortOrder = true;
      return this.sortByStudentName(a, b);
    }
    if (diff < 0) {
      return this.isAscending ? -1 : 1;
    }
    return this.isAscending ? 1 : -1;
  }

  sortByPointEarned(a, b) {
    let totalPointA = 0;
    let totalPointB = 0;
    const studentAResult = this.resultsByUser[a.id];
    const studentBResult = this.resultsByUser[b.id];
    this.useTieSortOrder = false;
    // for no result push to bottom
    const shouldStayBottom = TableSorter.putNullEmptyBelow(
      studentAResult,
      studentBResult,
    );
    if (shouldStayBottom) {
      return shouldStayBottom;
    }
    // when there is a No-Result tie
    if (shouldStayBottom === null) {
      this.useTieSortOrder = true;
      return this.sortByStudentName(a, b);
    }

    totalPointA = parseFloat(studentAResult.score);
    totalPointB = parseFloat(studentBResult.score);
    if (totalPointA === totalPointB) {
      // sort on student name if tie
      this.useTieSortOrder = true;
      return this.sortByStudentName(a, b);
    }
    if (this.isAscending) {
      return totalPointA < totalPointB ? -1 : 1;
    }
    return totalPointA < totalPointB ? 1 : -1;
  }

  sortByResponse(questionType, others) {
    this.useTieSortOrder = false;
    const sortType = others[others.length - 1];
    this.isAscending = sortType === 'ascending';
    this.isUnsorted = sortType === 'unsorted';

    const isMCQ = questionType === 'MCQ';
    const learnosityItemReference = others
      .slice(0, others.length - 1)
      .join('_');

    const getScore = (itemResponse) => {
      if (
        itemResponse &&
        typeof itemResponse === 'object' &&
        !Array.isArray(itemResponse)
      ) {
        itemResponse = itemResponse.score && itemResponse.score.toString();
      }
      return itemResponse;
    };

    const getScoreOrChoice = (response) => {
      if (Array.isArray(response)) {
        return response.join(',');
      }
      return response.toString();
    };

    const getStudentOrTeacherResults = (a, b, selectedValue) => {
      let resultsA;
      let resultsB;

      if (selectedValue === 'student') {
        resultsA = get(this.studentScoringResultByUser, `${a.id}.items`, {});
        resultsB = get(this.studentScoringResultByUser, `${b.id}.items`, {});
        return [
          resultsA[learnosityItemReference],
          resultsB[learnosityItemReference],
        ];
      }

      resultsA = get(this.teacherScoringResultByUser, `${a.id}.items`, {});
      resultsB = get(this.teacherScoringResultByUser, `${b.id}.items`, {});
      return [
        resultsA[learnosityItemReference],
        resultsB[learnosityItemReference],
      ];
    };

    const getItemResults = (a, b) => {
      let resultsA;
      let resultsB;

      if (isMCQ) {
        resultsA = get(this.itemResultsByUser, `${a.id}.items`, {});
        resultsB = get(this.itemResultsByUser, `${b.id}.items`, {});
        return [
          resultsA[learnosityItemReference],
          resultsB[learnosityItemReference],
        ];
      }

      if (this.showScoringDropdown) {
        const selectedValue = get(
          this.selectedScoringDropdownItem,
          'value',
          'teacher',
        );
        return getStudentOrTeacherResults(a, b, selectedValue);
      }

      resultsA = get(this.rubricResultByUser, `${a.id}.items`, {});
      resultsB = get(this.rubricResultByUser, `${b.id}.items`, {});
      return [
        resultsA[learnosityItemReference],
        resultsB[learnosityItemReference],
      ];
    };

    const compareResponses = (a, b, responseA, responseB) => {
      this.useTieSortOrder = false;
      let itemResponseA = responseA;
      let itemResponseB = responseB;

      const [itemResultA, itemResultB] = getItemResults(a, b);

      if (itemResultA) {
        if (this.isPreApSubject) {
          itemResponseA = itemResultA.score.toString() || NO_ANSWER_LABEL; // skipped question
        } else {
          itemResponseA = itemResponseA
            ? getScoreOrChoice(itemResponseA)
            : NO_ANSWER_LABEL; // skipped question
        }
      } else {
        itemResponseA = null;
      }

      if (itemResultB) {
        if (this.isPreApSubject) {
          itemResponseB = itemResultB.score.toString() || NO_ANSWER_LABEL; // skipped question
        } else {
          itemResponseB = itemResponseB
            ? getScoreOrChoice(itemResponseB)
            : NO_ANSWER_LABEL; // skipped question
        }
      } else {
        itemResponseB = null;
      }

      /**
       * for TEI question we sort based on the result status
       * since we can compare the svg icons
       * checkmark is correct = 1
       * X is incorrect = 0
       */
      const isTEI = this.listOfTEIs.includes(learnosityItemReference);
      if (isTEI) {
        if (itemResultA && itemResponseA && itemResponseA !== NO_ANSWER_LABEL) {
          itemResponseA = itemResultA.correct ? '1' : '0';
        }
        if (itemResultB && itemResponseB && itemResponseB !== NO_ANSWER_LABEL) {
          itemResponseB = itemResultB.correct ? '1' : '0';
        }
      }
      //  for other ColumnSort if there is a tie then sort on student name
      const sortResult = this.compare(itemResponseA, itemResponseB);
      if (sortResult === 0 || sortResult === null) {
        this.useTieSortOrder = true;
        return this.sortByStudentName(a, b);
      }

      return sortResult;
    };

    const getItemResponse = (a, b) => {
      if (this.showScoringDropdown) {
        return getItemResults(a, b);
      }

      let itemResponseA = (this.scoringRubricScoresByUser[a.id] || {})[
        learnosityItemReference
      ];
      let itemResponseB = (this.scoringRubricScoresByUser[b.id] || {})[
        learnosityItemReference
      ];
      return [itemResponseA, itemResponseB];
    };

    const sortOnFRQ = (a, b) => {
      const [itemResponseA, itemResponseB] = getItemResponse(a, b);
      return compareResponses(
        a,
        b,
        getScore(itemResponseA),
        getScore(itemResponseB),
      );
    };

    const sortOnMCQ = (a, b) => {
      let itemResponseA = (this.responsesByUser[a.id] || {})[
        learnosityItemReference
      ];
      let itemResponseB = (this.responsesByUser[b.id] || {})[
        learnosityItemReference
      ];
      return compareResponses(a, b, itemResponseA, itemResponseB);
    };
    return isMCQ ? sortOnMCQ : sortOnFRQ;
  }

  static putNullEmptyBelow(a, b) {
    // compare null, undefined for No result tie
    if (!a && !b) {
      return null;
    }
    if (!a) {
      return 1;
    } else if (!b) {
      return -1;
    }
  }
}

export default TableSorter;
